import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { api, getToken } from "@/lib/api";

/* FAVORITE STOP-OFFS — brand-level faves saved to the DRIVER ACCOUNT (same
   list on every device). The fullscreen map's FAVE button hunts the nearest
   matching stop ahead along the active corridor and pins it as the next
   stop. Examples: "pilot truck stop", "ta", "waffle house", a diner address. */

export default function FavesSection() {
    const navigate = useNavigate();
    const [faves, setFaves] = useState(null); // null = still loading
    const [needsLogin, setNeedsLogin] = useState(false);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        // No session token = anonymous visitor: skip the authed call entirely
        // (it can only 401 — that noise was flooding the backend log on every
        // landing-page view) and show the login prompt straight away.
        if (!getToken()) {
            setNeedsLogin(true);
            return;
        }
        api("/auth/faves")
            .then((d) => setFaves(d.faves || []))
            .catch(() => setNeedsLogin(true));
    }, []);

    const addFave = async () => {
        const label = input.trim();
        if (!label || busy) return;
        setBusy(true);
        try {
            const d = await api("/auth/faves", { method: "POST", body: { label } });
            setFaves(d.faves || []);
            setInput("");
        } catch (e) {
            if (String(e?.message).toLowerCase().includes("auth")) setNeedsLogin(true);
        } finally {
            setBusy(false);
        }
    };

    const removeFave = async (label) => {
        try {
            const d = await api("/auth/faves", { method: "DELETE", body: { label } });
            setFaves(d.faves || []);
        } catch {}
    };

    return (
        <section data-testid="faves-section" className="border-t-2 border-[#0E0D0C] bg-[#1B1917] py-20">
            <div className="mx-auto max-w-3xl px-5">
                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                >
                    <div className="flex items-center gap-4">
                        <span className="flex h-12 w-12 items-center justify-center border-2 border-[#0E0D0C] bg-[#E5A93C] shadow-[3px_3px_0px_#0E0D0C]">
                            <Star className="h-6 w-6 text-[#161413]" strokeWidth={2} />
                        </span>
                        <div>
                            <h2 className="font-display text-2xl uppercase tracking-widest text-[#F6F2E8] sm:text-3xl">
                                FAVORITE STOP-OFFS
                            </h2>
                            <p className="font-mono2 text-[10px] font-bold tracking-[0.3em] text-[#E5A93C]">
                                SAVED TO YOUR ACCOUNT — EVERY DEVICE
                            </p>
                        </div>
                    </div>
                    <p className="font-mono2 mt-5 max-w-2xl text-sm leading-relaxed tracking-wider text-[#C2C7CE]">
                        Save the names you always hunt — Pilot, TA, your diner. On the big map, one tap of the
                        FAVE button finds the nearest one AHEAD on your route and pins it as your next stop.
                    </p>

                    {needsLogin ? (
                        <div className="mt-6 border-2 border-[#E5A93C]/40 bg-[#24211E] p-5 text-center shadow-[4px_4px_0px_#0E0D0C]">
                            <p className="font-mono2 text-xs tracking-widest text-[#C2C7CE]">
                                Favorites live on your driver account so they follow you to every device.
                            </p>
                            <button
                                data-testid="faves-login-btn"
                                onClick={() => navigate("/login")}
                                className="btn-press font-display mt-4 border-2 border-[#0E0D0C] bg-[#D32F2F] px-5 py-3 text-base tracking-widest text-[#F6F2E8] shadow-[3px_3px_0px_#0E0D0C]"
                            >
                                LOG IN TO SAVE FAVES
                            </button>
                        </div>
                    ) : (
                        <div className="enamel-card mt-6 border-2 border-[#E5A93C]/40 bg-[#24211E] p-5 shadow-[4px_4px_0px_#0E0D0C]">
                            <div className="flex gap-2">
                                <input
                                    data-testid="faves-input"
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && addFave()}
                                    placeholder="pilot truck stop · ta · waffle house · an address"
                                    className="font-mono2 min-w-0 flex-1 border-2 border-[#0E0D0C] bg-[#161413] px-3 py-3 text-sm tracking-wider text-[#F6F2E8] placeholder:text-[#5f584e] focus:border-[#E5A93C] focus:outline-none"
                                />
                                <button
                                    data-testid="faves-add-btn"
                                    onClick={addFave}
                                    disabled={busy || !input.trim()}
                                    className="btn-press font-display border-2 border-[#0E0D0C] bg-[#E5A93C] px-4 py-3 text-base tracking-widest text-[#161413] shadow-[3px_3px_0px_#0E0D0C] disabled:opacity-50"
                                >
                                    SAVE FAVE
                                </button>
                            </div>
                            <div className="mt-4 flex flex-wrap gap-2">
                                {(faves || []).map((f, i) => (
                                    <span
                                        key={f}
                                        data-testid={`fave-chip-${i}`}
                                        className="font-mono2 inline-flex items-center gap-2 border-2 border-[#0E0D0C] bg-[#0055A5] px-3 py-2 text-xs font-bold uppercase tracking-widest text-[#F6F2E8] shadow-[2px_2px_0px_#0E0D0C]"
                                    >
                                        ★ {f}
                                        <button
                                            data-testid={`fave-del-${i}`}
                                            onClick={() => removeFave(f)}
                                            className="text-[#F3B7B0] hover:text-[#D32F2F]"
                                            aria-label={`remove ${f}`}
                                        >
                                            ✕
                                        </button>
                                    </span>
                                ))}
                                {faves && faves.length === 0 && (
                                    <p className="font-mono2 text-[11px] tracking-widest text-[#8C8377]">
                                        Nothing saved yet — add your go-to stop above.
                                    </p>
                                )}
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>
        </section>
    );
}
